import { push } from "connected-react-router";
import { Field, Form, Formik } from "formik";
import { isEmpty } from "lodash";
import moment from "moment";
import Button from "pages/_components/Button";
import GridTable from "pages/_components/GridTable/GridTable";
import Notification from "pages/_components/Notification";
import PageLoading from "pages/_components/PageLoading";
import TextField from "pages/_components/fields/TextField";
import AmountField from "pages/_components/fields/formik/AmountField";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import Selector from "pages/_components/fields/formik/Selector";
import SwitchField from "pages/_components/fields/formik/SwitchField";
import Scheduler from "pages/forms/_components/_fields/Scheduler";
import { bool } from "prop-types";
import React, { useState, useEffect } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { actions as transferActions } from "reducers/transfers";
import gaEventTracker, { events } from "util/gaEventTracker";
import * as i18n from "util/i18n";
import * as schedulerUtils from "util/scheduler";
import { transferReasons, transferTypes } from "util/transfer";
import I18n from "pages/_components/I18n";
import { useAccount } from "hooks/useAccounts";
import CustomData from "pages/_components/CustomData/CustomData";
import * as config from "util/config";
import useDebounce from "../hooks/useDebounce";
import { useOtherFieldChange } from "../hooks/useOtherFieldChange";
import { useOtherValidations } from "../hooks/useOtherValidations";
import { useTransferAccounts } from "../hooks/useTransferAccounts";
import Beneficiaries from "./components/Beneficiaries";

const FORM_ID = "transfer";

const OtherTransfersPage = ({ isDesktop }) => {
    const dispatch = useDispatch();
    const { lang } = useSelector((state) => state.i18n);
    // se setea scopteToShow a otherTransfers ya que cuentas Bibank 090 solo se usa en transferencias internas
    const { debitAccountOptions, fetching, accounts } = useTransferAccounts("otherTransfers");
    const { formRef, onChangeDebounce, setText } = useDebounce();
    const transfers = useSelector((state) => state.transfers);
    const transferData = transfers.otherTransferData;
    const transferKindOptions = i18n.formatOption(transferTypes, "transfer.types");
    const conceptOptions = i18n.formatOption(transferReasons, "transfer.reasons");
    const { state } = useLocation();
    const { programTrasferDataOther } = useSelector((sta) => sta.transfers);
    const { prevRoute } = useSelector((_state) => _state.form);
    const { prevPath = "" } = prevRoute;
    const fromContactPage = state?.fromContactPage;
    const fromAccountPage = prevPath.split("/").includes("accounts");

    const { otherTransferSchema, validateAccounts } = useOtherValidations(accounts);

    const { allowedTransfer } = transfers;

    const STARTING_HOUR = config.getInteger("bibank.transfer.closingHourStart", 16);

    const debitAccountOptionsFiltered = debitAccountOptions.filter((obj) => !obj.label.includes("CA U$D"));

    const {
        handleDebitAccount,
        handleAccountType,
        handleBeneficiary,
        handleConcept,
        handleChangeEmail,
        handleChangeMessage,
        handleOnChangeAmount,
    } = useOtherFieldChange(accounts, transferData, state);

    const currencies = [
        { id: "ARS", label: i18n.get(`currency.label.ARS`) },
        { id: "USD", label: i18n.get(`currency.label.USD`) },
    ];

    const [prefixValue, setPrefixValue] = useState();

    const data = {
        enabledWeekDays: [1, 2, 3, 4, 5, 6, 7],
        firstWorkingDate: moment(),
        maxDaysToSchedule: 10000,
        nonWorkingDays: [],
        lang,
        mode: "edit",
        programable: true,
        schedulable: true,
        // activeEnvironment,
    };

    const initialValues = {
        // eslint-disable-next-line no-nested-ternary
        debitAccount: fromContactPage
            ? "" || transferData?.debitAccount // cuando lleguen por la agenda esto evita que se pierda la seleccion actual del combo
            : fromAccountPage
            ? state?.accountId || ""
            : transferData?.debitAccount || "",
        creditCbu: fromContactPage || fromAccountPage ? state?.beneficiary?.cbu || "" : transferData?.creditCbu || "",
        transferKind:
            fromContactPage || fromAccountPage
                ? transferTypes[state?.beneficiary?.isOwn] || ""
                : transferData?.transferKind || "",
        amountField: {
            amount: fromContactPage || fromAccountPage ? null : transferData?.amountField?.amount || null,
            // eslint-disable-next-line no-nested-ternary
            currency: fromContactPage
                ? transferData?.amountField?.currency || "ARS"
                : fromAccountPage
                ? state?.currency || "ARS"
                : transferData?.amountField?.currency || "ARS",
        },
        reasonCode: fromContactPage || fromAccountPage ? "VAR" : transferData?.reasonCode || "VAR",
        reference: fromContactPage || fromAccountPage ? "" : transferData?.reference || "",
        reason: fromContactPage || fromAccountPage ? "" : transferData?.reason || "",
        creditCuit: fromContactPage || fromAccountPage ? "" : transferData?.creditCuit || "",
        nameRecipient:
            fromContactPage || fromAccountPage
                ? state?.beneficiary?.recipientName || ""
                : transferData?.nameRecipient || "",
        beneficiary: {
            productType:
                fromContactPage || fromAccountPage
                    ? state?.beneficiary?.productType || ""
                    : transferData?.beneficiary?.productType || "",
            localBank:
                fromContactPage || fromAccountPage
                    ? state?.beneficiary?.localBank || ""
                    : transferData?.beneficiary?.localBank || "",
            accountNumber:
                fromContactPage || fromAccountPage
                    ? state?.beneficiary?.accountNumber || ""
                    : transferData?.beneficiary?.accountNumber || "",
            recipientName:
                fromContactPage || fromAccountPage
                    ? state?.beneficiary?.recipientName || ""
                    : transferData?.beneficiary?.recipientName || "",
            recipientDocumentType:
                fromContactPage || fromAccountPage
                    ? state?.beneficiary?.recipientDocumentType || ""
                    : transferData?.beneficiary?.recipientDocumentType || "",
            recipientDocumentNumber:
                fromContactPage || fromAccountPage
                    ? state?.beneficiary?.recipientDocumentNumber || ""
                    : transferData?.beneficiary?.recipientDocumentNumber || "",
            recipientEmail:
                fromContactPage || fromAccountPage
                    ? state?.beneficiary?.recipientEmail || ""
                    : transferData?.beneficiary?.recipientEmail || "",
            cbu:
                fromContactPage || fromAccountPage
                    ? state?.beneficiary?.cbu || ""
                    : transferData?.beneficiary?.cbu || "",
            isOwn:
                fromContactPage || fromAccountPage
                    ? state?.beneficiary?.isOwn || ""
                    : transferData?.beneficiary?.isOwn || "",
        },
        frequentDestination: fromContactPage || fromAccountPage ? false : transferData?.frequentDestination || false,
        scheduler: fromContactPage || fromAccountPage ? null : programTrasferDataOther || null,
        sendEmail: fromContactPage || fromAccountPage ? false : transferData?.sendEmail || false,
        email: fromContactPage || fromAccountPage ? "" : transferData?.email || "",
        messageText: fromContactPage || fromAccountPage ? "" : transferData?.emailText || "",
    };

    const { selectedAccount: _debitAccount, formatAccountBody } = useAccount("productId", transferData.debitAccount);

    const handleSubmit = (values, formikBag) => {
        const isValidAccounts = validateAccounts(values.transferKind, _debitAccount?.currency, values.beneficiary);
        const otherTransferData = {
            ...values,
            emailText: values?.messageText,
            amount: {
                quantity: values.amountField.amount,
                currency: values.amountField.currency,
            },
            scheduler:
                values.scheduler.selectedOption !== schedulerUtils.TODAY
                    ? values.scheduler || programTrasferDataOther
                    : null,
            debitAccountLabel: formatAccountBody(_debitAccount),
            ownerName: _debitAccount?.ownerName,
            beneficiaryAccountLabel: values.beneficiary
                ? formatAccountBody({
                      accountNumber: values.beneficiary?.accountNumber,
                      accountType: values.beneficiary?.productType.split(" ")[0],
                      currency: values.beneficiary?.productType.split(" ")[1],
                  })
                : "",
        };

        if (isValidAccounts) {
            dispatch(transferActions.setOtherTransferData(otherTransferData));

            dispatch(
                push({
                    pathname: `/transfers/other/confirm`,
                    state,
                }),
            );
        }
        formikBag.setSubmitting(false);
    };

    const programTransfer = (value) => {
        gaEventTracker.triggerEvent(events.programar({ frecuencia: value?.frequency }));
    };

    useEffect(() => {
        dispatch(transferActions.generateClosingHourTransferValidation());
    }, []);

    return (
        <>
            <Notification scopeToShow="otherTransfers" />
            <PageLoading loading={fetching}>
                <Formik
                    initialValues={initialValues}
                    onSubmit={handleSubmit}
                    validationSchema={otherTransferSchema}
                    ref={formRef}>
                    {({ values, setFieldValue, isSubmitting }) => {
                        const validAccount = debitAccountOptions.find((account) => account.id === values.debitAccount);
                        const canSubmit =
                            !values.debitAccount ||
                            !values.transferKind ||
                            !values.creditCbu ||
                            !values.amountField.amount ||
                            !values.reasonCode ||
                            !values.beneficiary.cbu ||
                            (!values.email && values.sendEmail) ||
                            isEmpty(validAccount);
                        setPrefixValue(
                            validAccount?.balance?.currency === "ARS"
                                ? i18n.get(`currency.label.ARS`)
                                : i18n.get(`currency.label.USD`),
                        );
                        return (
                            <Form>
                                <GridTable className="form transfer-others">
                                    <GridTable.Container columns={12} columnsTemplate="fixed">
                                        <GridTable.Data columnStart={2} columnWidth={10} className="required">
                                            <I18n component="h3" id={`${FORM_ID}.debitAccount.label`} />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={10}>
                                            <Field
                                                component={ProductSelector}
                                                name="debitAccount"
                                                placeholder={i18n.get("global.select.account.placeholder")}
                                                data={{ emptyOptionLabel: "", options: debitAccountOptionsFiltered }}
                                                onChange={(id) => handleDebitAccount(id, setFieldValue, values)}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={10}>
                                            <I18n component="h3" id={`${FORM_ID}.creditAccount.label`} />
                                        </GridTable.Data>
                                        <GridTable.Data className="required" columnStart={2} columnWidth={3}>
                                            <Field
                                                component={Selector}
                                                label={i18n.get(`${FORM_ID}.transferKind.label`)}
                                                name="transferKind"
                                                disabled={!values.debitAccount || values.beneficiary.cbu}
                                                options={transferKindOptions.reverse()}
                                                placeholder={i18n.get("global.select.option.placeholder")}
                                                onChange={(type) => handleAccountType(type, values)}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data className="required" columnStart={5} columnWidth={4}>
                                            <Field
                                                component={TextField}
                                                labelnoI18n={i18n.get(`${FORM_ID}.creditAccountCbuNumber.label`)}
                                                idForm={FORM_ID}
                                                name="creditCbu"
                                                disabled={
                                                    !values.debitAccount || values.beneficiary.cbu || isSubmitting
                                                }
                                                type="text"
                                                hidePlaceholder
                                                handleOnChange={(e) => {
                                                    onChangeDebounce(e.target.value);
                                                    dispatch(
                                                        transferActions.setOtherTransferData({
                                                            ...values,
                                                            creditCbu: e.target.value,
                                                        }),
                                                    );
                                                }}
                                            />
                                        </GridTable.Data>
                                        <Beneficiaries
                                            isDesktop={isDesktop}
                                            values={values}
                                            setValues={setFieldValue}
                                            setText={setText}
                                            onChange={(e) => handleBeneficiary(e, values)}
                                        />
                                        <GridTable.Data
                                            className="required"
                                            columnStart={2}
                                            columnWidth={isDesktop ? 5 : 10}>
                                            <Field
                                                component={AmountField}
                                                labelText={i18n.get(`${FORM_ID}.amountField.label`)}
                                                name="amountField"
                                                disableNumber={!values.debitAccount}
                                                data={{ options: currencies }}
                                                clearable={false}
                                                integerLength={10}
                                                decimalLength={2}
                                                onBlurChange={(amount) =>
                                                    handleOnChangeAmount(amount, values, setFieldValue)
                                                }
                                                withValueCap
                                                disableSelect
                                                fixedDecimalScale
                                                hideCurrency
                                                prefix={prefixValue}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            columnStart={isDesktop ? 7 : 2}
                                            columnWidth={isDesktop ? 5 : 10}>
                                            <Field
                                                component={Selector}
                                                label={i18n.get(`${FORM_ID}.concept.label`)}
                                                name="reasonCode"
                                                options={conceptOptions}
                                                placeholder={i18n.get("global.select.option.placeholder")}
                                                onChange={(e) => handleConcept(e, values)}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data columnStart={2} columnWidth={isDesktop ? 5 : 10}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="reference"
                                                type="text"
                                                hidePlaceholder
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            columnStart={isDesktop ? 7 : 2}
                                            columnWidth={isDesktop ? 5 : 10}>
                                            <Field
                                                component={TextField}
                                                idForm={FORM_ID}
                                                name="reason"
                                                type="text"
                                                hidePlaceholder
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            className={!isDesktop && "btn-full-width"}
                                            columnStart={2}
                                            columnWidth={10}
                                            flexDirection="column">
                                            <Field
                                                component={Scheduler}
                                                data={data}
                                                name="scheduler"
                                                showLabel={false}
                                                persisDataOther
                                                dispatch={dispatch}
                                                programTrasferDataOther={programTrasferDataOther}
                                                showAsSidebar
                                                handleChange={programTransfer}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            columnStart={2}
                                            columnWidth={10}
                                            alignY="center"
                                            className="h-full">
                                            <Field
                                                component={SwitchField}
                                                idForm={FORM_ID}
                                                name="sendEmail"
                                                onChange={() => {
                                                    dispatch(
                                                        transferActions.setOtherTransferData({
                                                            ...values,
                                                            sendEmail: !values.sendEmail,
                                                        }),
                                                    );
                                                }}
                                            />
                                        </GridTable.Data>
                                        {values.sendEmail && (
                                            <>
                                                <GridTable.Data columnStart={2} columnWidth={5}>
                                                    <Field
                                                        component={TextField}
                                                        labelnoI18n={i18n.get("global.email.label")}
                                                        idForm={FORM_ID}
                                                        name="email"
                                                        type="email"
                                                        placeholder={i18n.get("global.email.placeholder")}
                                                        handleOnChange={(email) => handleChangeEmail(email, values)}
                                                    />
                                                </GridTable.Data>
                                                <GridTable.Data columnStart={7} columnWidth={5}>
                                                    <Field
                                                        component={TextField}
                                                        idForm={FORM_ID}
                                                        name="messageText"
                                                        maxLength="500"
                                                        type="text"
                                                        placeholder={i18n.get("global.message.placeholder")}
                                                        handleOnChange={(message) =>
                                                            handleChangeMessage(message, values)
                                                        }
                                                    />
                                                </GridTable.Data>
                                            </>
                                        )}
                                    </GridTable.Container>
                                </GridTable>

                                <div className="container--alert" role="alert">
                                    <div className="alert alert-info">
                                        <CustomData
                                            icon="information_fill.svg"
                                            titleText={i18n.get("transfer.otherBank.disclaimer", null, {
                                                STARTING_HOUR,
                                            })}
                                            keepSpaceIfNoValue={false}
                                        />
                                    </div>
                                </div>

                                <Row className="form-actions">
                                    <Col xs={12} md={4} mdOffset={4}>
                                        <Button
                                            type="submit"
                                            className="btn-primary"
                                            label="global.continue"
                                            loading={isSubmitting}
                                            disabled={
                                                canSubmit || (!allowedTransfer && programTrasferDataOther == null)
                                            }
                                        />
                                    </Col>
                                </Row>
                            </Form>
                        );
                    }}
                </Formik>
            </PageLoading>
        </>
    );
};

OtherTransfersPage.propTypes = {
    isDesktop: bool.isRequired,
};

export default OtherTransfersPage;
